import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "../pages/LoginPage";
import CustomerManagementPage from "../pages/CustomerManagementPage";
import CustomerListPage from "../pages/CustomerListPage";
import SubaccountManagementPage from "../pages/SubaccountManagementPage";
import ProtectedRoute from "../components/ProtectedRoute";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Box, CircularProgress } from "@mui/material";
import SessionExpiredModal from "../components/SessionExpiredModal";
import { fetchAdminDetails, setupActivityListeners, logout, startTokenRefresh, resetInactivityTimer } from "../api/authApi";
import DashboardOverview from "../components/DashboardOverview";
import AdminUsersPage from "../pages/AdminUsersPage";
import ProductManagementPage from "../pages/ProductManagementPage";
import AddEditProductPage from "../pages/AddEditProductPage";
import OrdersPage from "../pages/OrdersPage";
import OrderDetailsPage from "../pages/OrderDetailsPage";
import CreateCustomerPage from "../pages/CreateCustomerPage";
import PricingManager from "../pages/PricingManager";
import AnalyticsOverview from "../pages/AnalyticsOverview";
import SalesReports from "../pages/SalesReports";
import UserAnalytics from "../pages/UserAnalytics";
import RealtimeAnalytics from "../pages/RealtimeAnalytics";
import CustomerOverviewPage from "../pages/CustomerOverviewPage";
import BrandsPage from "../pages/BrandsPage";
import CategoriesPage from '../pages/CategoryPage';


const AppRoutes: React.FC = () => {
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionExpired, setSessionExpired] = useState(false);

  const handleMenuClick = () => {
    setDrawerOpen(!isDrawerOpen);
  };

  const handleSessionExpired = () => {
    setSessionExpired(true);
  };

  const handleModalClose = () => {
    logout();
    setSessionExpired(false);
  };
  const [isCollapsed, setIsCollapsed] = useState(false);


useEffect(() => {
  // Initialize activity listeners once
  setupActivityListeners();
  resetInactivityTimer(); // Initialize inactivity timer

  const validateInitialSession = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      await fetchAdminDetails();
      // Start token refresh only after successful validation
      startTokenRefresh();
      setIsLoading(false);
    } catch (error) {
      console.error("Initial session validation failed:", error);
      setIsLoading(false);
    }
  };

  validateInitialSession();
}, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router>
      {isSessionExpired && (
        <SessionExpiredModal
          open={isSessionExpired}
          onClose={handleModalClose}
        />
      )}

      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            localStorage.getItem("token") ? (
              <Navigate to="/dashboard/overview" replace />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Protected Routes */}
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <Box sx={{ display: "flex", height: "100vh" }}>
                <Header onMenuClick={handleMenuClick} onLogout={logout} />
<Sidebar
  isDrawerOpen={isDrawerOpen} 
  setDrawerOpen={setDrawerOpen}
  isCollapsed={isCollapsed}
  setIsCollapsed={setIsCollapsed}
/>                <Box sx={{ flexGrow: 1, padding: 3, marginTop: 8 }}>
                  <Routes>
                    <Route path="/" element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<DashboardOverview />} />
                    <Route path="analytics" element={<Navigate to="overview" replace />} />
                    <Route path="analytics/overview" element={<AnalyticsOverview />} />
                    <Route path="analytics/sales-reports" element={<SalesReports />} />
                    <Route path="analytics/user-analytics" element={<UserAnalytics />} />
                    <Route path="analytics/realtime" element={<RealtimeAnalytics />} />
                    <Route path="customers" element={<CustomerManagementPage />} />
                    <Route path="customers/create" element={<CreateCustomerPage />} />
                    <Route path="customers/list" element={<CustomerListPage />} />
                    <Route path="customers/:id/overview" element={<CustomerOverviewPage />} />
                    <Route path="customers/subaccounts" element={<SubaccountManagementPage />} />
                    <Route path="admin-users" element={<AdminUsersPage />} />
                    <Route path="products" element={<ProductManagementPage />} />
                    <Route path="products/add" element={<AddEditProductPage />} />
                    <Route path="products/edit/:id" element={<AddEditProductPage isEditing={true} />} />
                    <Route path="orders" element={<OrdersPage />} />
                    <Route path="orders/:id" element={<OrderDetailsPage />} />
                    <Route path="pricing-manager" element={<PricingManager />} />
                    <Route path="brands" element={<BrandsPage />} />
                    <Route path="categories" element={<CategoriesPage />} />
                  </Routes>
                </Box>
              </Box>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
};

export default AppRoutes;